import type { Condition } from '@seen/core/observation-logic/conditionSchema'
import { ConditionalSectionNode as ConditionalSectionSchema } from '@seen/core/tiptap/schema'
import type { Node as PMNode } from '@tiptap/pm/model'
import { TextSelection } from '@tiptap/pm/state'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { ConditionalSectionView } from '#src/components/rich-text-editor/conditional-display/ConditionalSectionView.tsx'
import { ownConditionTarget } from '#src/components/rich-text-editor/conditional-display/conditionalWrapKind.ts'
import { canConfigureNodes } from '#src/components/rich-text-editor/conditional-display/NodeConfigExtension.ts'

export interface ConditionalSectionOptions {
  // A template frames each section and names its condition in a chip.
  isTemplate: boolean
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    conditionalSection: {
      // Wraps the selected blocks (or `range`) in a new conditional section.
      wrapInConditionalSection: (range?: {
        from: number
        to: number
      }) => ReturnType
      // Puts the content of the section at `pos` (else the one around the
      // selection) back in place.
      unwrapConditionalSection: (pos?: number) => ReturnType
      setSectionCondition: (
        id: string,
        condition: Condition | null
      ) => ReturnType
    }
  }
}

export const ConditionalSectionNode =
  ConditionalSectionSchema.extend<ConditionalSectionOptions>({
    addOptions() {
      return { isTemplate: false }
    },

    addNodeView() {
      return ReactNodeViewRenderer(ConditionalSectionView)
    },

    addCommands() {
      return {
        wrapInConditionalSection:
          (range) =>
          ({ tr, commands }) => {
            if (range) {
              tr.setSelection(
                TextSelection.create(tr.doc, range.from, range.to)
              )
            }
            // Wrapping exactly what a section already holds (or a lone
            // section) would only stack an identical frame around it.
            const blocks = tr.selection.$from.blockRange(tr.selection.$to)
            if (
              blocks &&
              ((blocks.parent.type === this.type &&
                blocks.startIndex === 0 &&
                blocks.endIndex === blocks.parent.childCount) ||
                (blocks.endIndex - blocks.startIndex === 1 &&
                  blocks.parent.child(blocks.startIndex).type === this.type))
            ) {
              return false
            }
            return commands.wrapIn(this.name, { id: crypto.randomUUID() })
          },
        unwrapConditionalSection:
          (pos) =>
          ({ tr, dispatch }) => {
            const at = pos === undefined ? undefined : tr.doc.nodeAt(pos)
            const found =
              pos !== undefined && at?.type.name === this.name
                ? { node: at, pos }
                : pos === undefined
                  ? sectionAround(tr.doc, tr.selection.from, this.name)
                  : undefined
            if (!found) {
              return false
            }
            if (dispatch) {
              tr.replaceWith(
                found.pos,
                found.pos + found.node.nodeSize,
                found.node.content
              )
            }
            return true
          },
        setSectionCondition:
          (id, condition) =>
          ({ tr, dispatch }) => {
            let pos: number | undefined
            tr.doc.descendants((node, at) => {
              if (node.type.name === this.name && node.attrs.id === id) {
                pos = at
              }
              return pos === undefined
            })
            if (pos === undefined) {
              return false
            }
            if (dispatch) {
              tr.setNodeAttribute(pos, 'showWhen', condition)
            }
            return true
          },
      }
    },

    addKeyboardShortcuts() {
      return {
        'Mod-Shift-k': () => {
          const { doc, selection } = this.editor.state
          const own = canConfigureNodes(this.editor)
            ? ownConditionTarget(doc, selection.from, selection.to)
            : null
          if (own !== null) {
            return this.editor.commands.openNodeConfig(own)
          }
          const inside = sectionAround(doc, selection.from, this.name)
          return inside && selection.empty
            ? this.editor.commands.unwrapConditionalSection()
            : this.editor.commands.wrapInConditionalSection()
        },
      }
    },
  })

function sectionAround(
  doc: PMNode,
  pos: number,
  name: string
): { node: PMNode; pos: number } | undefined {
  const $pos = doc.resolve(pos)
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth)
    if (node.type.name === name) {
      return { node, pos: $pos.before(depth) }
    }
  }
  return undefined
}
